import {
  checkAdminStatus,
  checkSuperAdminStatus,
  checkUserSearchAccess
} from '@/services/super-admins-api.service.js'
import { logger } from '@/utils/logger'

// Cache for admin status checks with TTL
const CACHE_TTL = 12 * 60 * 60 * 1000 // 12 hours in milliseconds
const adminStatusCache = new Map()
const superAdminStatusCache = new Map()
const userSearchAccessCache = new Map()

function buildCacheKey(scope, email) {
  return `${scope}:${String(email).trim().toLowerCase()}`
}

/**
 * Get cached value if it exists and hasn't expired
 * @param {Map} cache - The cache map to check
 * @param {string} key - The cache key
 * @returns {boolean|null} Cached value or null if expired/missing
 */
function getCachedValue(cache, key) {
  const cached = cache.get(key)
  if (!cached) return null

  const now = Date.now()
  if (now - cached.timestamp > CACHE_TTL) {
    cache.delete(key)
    return null
  }

  return cached.value
}

/**
 * Set a value in the cache with current timestamp
 * @param {Map} cache - The cache map to update
 * @param {string} key - The cache key
 * @param {boolean} value - The value to cache
 */
function setCachedValue(cache, key, value) {
  cache.set(key, {
    value,
    timestamp: Date.now()
  })
}

/**
 * Clear all admin status caches (useful for logout or when admin status changes)
 */
export function clearAdminStatusCache() {
  adminStatusCache.clear()
  superAdminStatusCache.clear()
  userSearchAccessCache.clear()
}

/**
 * Check if the current authenticated user is an admin
 * Backend uses res.locals.user.email to prevent spoofing
 * Results are cached for 5 minutes to reduce API calls
 * @param {string} email - Email parameter kept for API compatibility
 * @returns {Promise<boolean>} True if user is an admin
 */
export async function isAdminUser(email) {
  if (!email) return false

  const cacheKey = buildCacheKey('admin-status', email)

  // Check cache first
  const cached = getCachedValue(adminStatusCache, cacheKey)
  if (cached !== null) {
    return cached
  }

  try {
    const isAdmin = await checkAdminStatus()
    setCachedValue(adminStatusCache, cacheKey, isAdmin)
    return isAdmin
  } catch (error) {
    logger.error('Error checking admin status:', error.message)
    return false
  }
}

/**
 * Check if the current authenticated user is a super admin
 * Backend uses res.locals.user.email to prevent spoofing
 * Results are cached for 5 minutes to reduce API calls
 * @param {string} email - Email parameter kept for API compatibility
 * @returns {Promise<boolean>} True if user is a super admin
 */
export async function isSuperAdminUser(email) {
  if (!email) return false

  const cacheKey = buildCacheKey('super-admin-status', email)

  // Check cache first
  const cached = getCachedValue(superAdminStatusCache, cacheKey)
  if (cached !== null) {
    return cached
  }

  try {
    const isSuperAdmin = await checkSuperAdminStatus()
    setCachedValue(superAdminStatusCache, cacheKey, isSuperAdmin)
    return isSuperAdmin
  } catch (error) {
    logger.error('Error checking super admin status:', error.message)
    return false
  }
}

/**
 * Check if the current authenticated user has strict access to the super admin user search tab
 * @param {string} email - Email parameter kept for API compatibility
 * @returns {Promise<boolean>} True if the user has explicit user search access
 */
export async function hasUserSearchAccess(email) {
  if (!email) return false

  const cacheKey = buildCacheKey('user-search-access', email)
  const cached = getCachedValue(userSearchAccessCache, cacheKey)
  if (cached !== null) {
    return cached
  }

  try {
    const hasAccess = await checkUserSearchAccess()
    setCachedValue(userSearchAccessCache, cacheKey, hasAccess)
    return hasAccess
  } catch (error) {
    logger.error('Error checking super admin user search access:', error)
    return false
  }
}
